import { Button, Field, OtpInput, Skeleton } from "@nabble/ui";
import { type FormEvent, useEffect, useState } from "react";
import { useOtpCode } from "../hooks/useOtpCode";
import { type TotpEnrollment, useTotpSecret } from "../hooks/useTotpSecret";
import { CopySecretButton } from "./CopySecretButton";

const TOTP_CODE_LENGTH = 6;

export interface TotpSetupProps {
  /** Starts enrollment on the server and returns the secret and otpauth URI. */
  start: () => Promise<TotpEnrollment>;
  /** Confirms the first code. Throw to show `error`. */
  confirm: (code: string) => Promise<void>;
  error?: string | null;
  onStartError?: (err: unknown) => void;
  submitLabel?: string;
}

function groupSecret(secret: string): string {
  return secret.replace(/(.{4})/g, "$1 ").trim();
}

/** QR card + manual key + 6-digit confirmation. Used in enrollment and in My account. */
export function TotpSetup({ start, confirm, error, onStartError, submitLabel = "Confirm" }: TotpSetupProps): React.JSX.Element {
  const { secret, qr } = useTotpSecret(start, onStartError);
  const { code, setCode, shake, reject } = useOtpCode();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (error) reject();
  }, [error, reject]);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (code.length !== TOTP_CODE_LENGTH) return;
    setLoading(true);
    try {
      await confirm(code);
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className="nb-auth-form" onSubmit={(e) => void submit(e)}>
      <div className="nb-qr">
        <div className="nb-qr-img">
          {qr ? <img src={qr} alt="QR code to set up your authenticator app" /> : <Skeleton height={136} radius={6} />}
        </div>
        <ol className="nb-qr-steps">
          <li>Open your authenticator app (Google Authenticator, Microsoft Authenticator, 1Password or another).</li>
          <li>Scan this QR code to add your Nabble Suite account.</li>
          <li>Enter the 6-digit code the app shows in the box below.</li>
        </ol>
      </div>
      <div className="nb-stack-sm">
        <span className="nb-help">Can't scan it? Enter this key in the app:</span>
        <div className="nb-secret">
          {secret ? (
            <code data-testid="totp-secret">{groupSecret(secret)}</code>
          ) : (
            <Skeleton width="70%" height={14} />
          )}
          <CopySecretButton secret={secret} />
        </div>
      </div>
      <Field label="6-digit code" error={error ?? undefined}>
        <OtpInput key={shake} value={code} onChange={setCode} shaking={shake > 0} autoFocus={shake > 0} disabled={!secret} />
      </Field>
      <Button type="submit" label={submitLabel} size="lg" block loading={loading} disabled={code.length !== TOTP_CODE_LENGTH} />
    </form>
  );
}
